import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { track, gaItem } from "@/lib/analytics";
import { Loader2 } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { ProductPanel, AddToCart } from "@/components/ProductPanel";
import { SOON } from "@/components/ComingSoon";
import { useSeo } from "@/lib/seo";
import { productSeo } from "@/data/seo";
import { IMAGES, CURRENCY, money, productImage } from "@/lib/api";
import NotFound from "@/pages/NotFound";
import { PRODUCT_COPY } from "@/data/content";
import { STACKS } from "@/data/stacks";
import { SINGLES } from "@/data/singles";
import { BUNDLES } from "@/data/bundles";
import { OceanPanel } from "@/components/Ocean";
import { StackPanel } from "@/components/Stacks";

const PRODUCT_IMAGE = { "fish-oil": IMAGES.bottle, multi: IMAGES.multi };
const SKU_FALLBACK = { "fish-oil": "GF-FISHOIL", multi: "GF-MULTI" };

const SEA_LD = "Irish sea moss, bladderwrack and burdock root, plus a pinch of black pepper extract. 1,405 mg per 2-capsule serving; 60 capsules, 30 servings.";
const LD_EXTRA = {
  "sea-moss": { image: "https://grandpafinns.com/products/sea-moss.webp", description: SEA_LD },
  "sea-moss-2-pack": { image: "https://grandpafinns.com/products/sea-moss-2-pack.webp", description: SEA_LD },
};

const productLd = (p, url) => ({
  "@context": "https://schema.org", "@type": "Product", name: p.name, image: new URL(PRODUCT_IMAGE[p.id] || productImage(p.id), window.location.origin).href, ...LD_EXTRA[p.id], sku: p.sku || SKU_FALLBACK[p.id] || p.id,
  brand: { "@type": "Brand", name: "Grandpa Finn's" },
  offers: { "@type": "Offer", url, price: p.price_usd.toFixed(2), priceCurrency: CURRENCY,
    availability: p.active && p.available > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
});

const useShowSticky = (id) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = document.querySelector(`[data-testid="add-to-cart-${id}"], [data-testid="add-to-cart-blocked-${id}"], [data-testid="members-only-block-${id}"]`);
    if (!el) { setShow(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setShow(!e.isIntersecting && e.boundingClientRect.top < 0));
    io.observe(el);
    return () => io.disconnect();
  }, [id]);
  return show;
};

const StickyBuy = ({ p }) => {
  const show = useShowSticky(p.id);
  return (
    <div data-testid={`sticky-buy-${p.id}`} data-visible={show} aria-hidden={!show} className={`sticky-buy lg:hidden fixed inset-x-0 bottom-0 z-30 border-t border-navy/10 bg-sand/95 backdrop-blur px-4 py-3 transition-transform duration-200 ${show ? "" : "sticky-buy-hidden"}`}>
      <div className="flex items-center gap-3">
        <div className="flex-auto min-w-0"><p data-testid={`sticky-buy-title-${p.id}`} className="sticky-buy-title font-semibold">{p.short_name}</p><p className="text-xs text-slate-600">{money(p.price_usd)} one-time</p></div>
        {p.active && p.available > 0 && !p.members_only && <div className="flex-none"><AddToCart p={p} compact /></div>}
      </div>
    </div>
  );
};

export default function ProductPage() {
  const { id } = useParams();
  const { products } = useStore();
  const p = products.find((x) => x.id === id && (PRODUCT_COPY[x.id] || STACKS[x.id] || SINGLES[x.id] || BUNDLES[x.id]));
  const seo = productSeo(id, p?.name);
  useSeo({ ...seo, jsonLd: p ? [productLd(p, seo.canonical)] : [], robots: products.length && !p ? "noindex" : undefined });
  const pid = p?.id;
  useEffect(() => {
    if (pid) track("view_item", { currency: CURRENCY, value: Number(p.price_usd), items: [gaItem(p)] });
  }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (SOON.some((c) => c.id === id)) return <Navigate to="/shop#coming-soon" replace />;
  if (products.length && !p) return <NotFound />;
  return (
    <div data-testid={`product-page-${id}`} className="pb-[calc(92px+env(safe-area-inset-bottom))] lg:pb-0">
      <div className="container-gf pt-8">
        <Link to="/shop" data-testid="product-back-to-shop" className="text-sm font-semibold underline decoration-sunny decoration-2 underline-offset-4">← All products</Link>
      </div>
      {p ? <>{SINGLES[p.id] || BUNDLES[p.id] ? <OceanPanel p={p} /> : STACKS[p.id] ? <StackPanel p={p} /> : <ProductPanel p={p} />}<StickyBuy p={p} /></> : <div className="min-h-[200vh] py-24 grid place-items-start justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>}
    </div>
  );
}
